import BoldText from '@/components/BoldText';
import { PlanMessage } from '@/components/PlanMessage';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { LOCALE_OPTIONS, PricingPlan } from '@/constants/enum';
import { languageList, languageToLocale } from '@/constants/language';
import { PATH } from '@/constants/path';
import { getLocalStorageItem, setLocalStorageItem } from '@/helpers/storage';
import { RevertDefaultModal } from '@/pages/Translation/component/Modal/RevertDefaultModal';
import { StyledFlexGrow } from '@/pages/WidgetAppearance/styled';
import { useGetTranslateTextMutation, useUpdateRevertTextMutation } from '@/redux/query';
import { handleSetSaveBar, saveBarSelector } from '@/redux/slice/savebar.slice';
import {
  handleSaveWidgetTextDefaultDetail,
  widgetStateSelector,
  widgetTextDefaultDetailSelector,
  widgetAppearanceDisableSelector,
} from '@/redux/slice/widget.slice';
import { BlockStack, Box, Button, InlineStack, Link, Select } from '@shopify/polaris';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export const WidgetTextFields = () => {
  const { t: tWidget } = useTranslation('widget_settings');
  const { t: tTrans } = useTranslation('translation');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const widgetTextDefaultDetail = useSelector(widgetTextDefaultDetailSelector);
  const [defaultLanguage, setDefaultLanguage] = useState(widgetTextDefaultDetail.code);
  const [getTranslateText, { isLoading: isLoadingTranslateText }] = useGetTranslateTextMutation();
  const [updateRevertText, { isLoading: isLoadingRevertText }] = useUpdateRevertTextMutation();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const saveBar = useSelector(saveBarSelector);
  const widgetAppearanceDisable = useSelector(widgetAppearanceDisableSelector);

  const localeOptions = useMemo(
    () =>
      Object.values(LOCALE_OPTIONS).map((option) => ({
        label: tTrans(`locale.options.${option}`),
        value: option,
      })),
    [tTrans],
  );

  const revertDefault = () => {
    updateRevertText({
      shop: config.shop,
      code: defaultLanguage || '',
      calendarLocale: widgetTextDefaultDetail.calendarLocale!,
    })
      .unwrap()
      .then((res) => {
        dispatch(
          handleSaveWidgetTextDefaultDetail({
            additionalMessage: res.data.additionalMessage,
            calendarLocale: languageToLocale[defaultLanguage || 'en-US'],
            code: defaultLanguage,
            customMessage: res.data.customMessage,
            headerTitle: res.data.headerTitle,
            name: languageList.find((item) => item.value === defaultLanguage)?.label,
            shop: config.shop,
            texts: res.data.texts,
          }),
        );
        setIsOpenModal(false);
      });
  };

  const handleTranslateText = () => {
    if (getLocalStorageItem('translated')) {
      revertDefault();
    } else {
      getTranslateText({
        code: defaultLanguage || '',
        headerTitle: widgetTextDefaultDetail.headerTitle || '',
        additionalMessage: widgetTextDefaultDetail.additionalMessage || '',
        customMessage: '',
        texts: widgetTextDefaultDetail.texts,
      })
        .unwrap()
        .then((res) => {
          const rawTexts = res.data.texts ?? [];

          const patchedTexts = rawTexts.map((item) => ({
            ...item,
            isUseAdditionalNote: false,
            isShowMessageMinimum: false,
          }));

          const headerTitleTrans = patchedTexts.find((item) => item?.headerTitle)?.headerTitle;
          const additionalMessageTrans = patchedTexts.find((item) => item?.additionalMessage)?.additionalMessage;

          const processedData = {
            headerTitle: headerTitleTrans,
            additionalMessage: additionalMessageTrans,
            texts: patchedTexts,
          };

          setLocalStorageItem('translated', 'true');

          dispatch(handleSaveWidgetTextDefaultDetail(processedData));
          dispatch(
            handleSaveWidgetTextDefaultDetail({
              code: defaultLanguage,
              name: languageList.find((item) => item.value === defaultLanguage)?.label,
              calendarLocale: languageToLocale[defaultLanguage || 'en-US'],
            }),
          );
        });
    }
  };

  useEffect(() => {
    if (saveBar.isDiscard) {
      setDefaultLanguage(widgetTextDefaultDetail.code);
      dispatch(handleSetSaveBar({ isDiscard: false }));
    }
  }, [dispatch, saveBar.isDiscard, widgetTextDefaultDetail.code]);

  return (
    <BlockStack gap={'300'}>
      <InlineStack align="space-between" blockAlign="center">
        <BoldText>{tWidget('widgetDesign.textField.textFields.title')}</BoldText>
        {/* <Link removeUnderline onClick={() => setIsOpenModal(true)}>
          {tWidget('widgetDesign.textField.textFields.reset')}
        </Link> */}
      </InlineStack>
      <BlockStack gap={'200'}>
        <InlineStack align="space-between" gap={'100'}>
          <RegularText>{tWidget('widgetDesign.textField.textFields.default')}</RegularText>
          {widgetAppearanceDisable.isDisableAutoTranslate && <PlanMessage plan={PricingPlan.Essential} />}
        </InlineStack>
        <InlineStack blockAlign={'start'} gap={'300'}>
          <StyledFlexGrow>
            <Select
              label=""
              labelHidden
              options={languageList}
              value={defaultLanguage}
              onChange={(value) => setDefaultLanguage(value)}
              placeholder={tWidget('widgetDesign.textField.textFields.languagePlaceHolder')}
              helpText={
                !widgetAppearanceDisable.isDisableAutoTranslate && (
                  <Box>
                    <RegularText>If you want more translation for widget, go</RegularText>{' '}
                    <Link onClick={() => navigate(PATH.TRANSLATION.pathname)} removeUnderline>
                      here
                    </Link>
                  </Box>
                )
              }
              disabled={widgetAppearanceDisable.isDisableAutoTranslate}
            />
          </StyledFlexGrow>
          <Box>
            <Button
              onClick={handleTranslateText}
              disabled={!defaultLanguage || widgetAppearanceDisable.isDisableAutoTranslate}
              loading={isLoadingTranslateText || isLoadingRevertText}
              size="large"
            >
              {tWidget('widgetDesign.textField.textFields.defaultBtn')}
            </Button>
          </Box>
        </InlineStack>
      </BlockStack>
      <Select
        label={tTrans('locale.label')}
        options={localeOptions}
        value={widgetTextDefaultDetail.calendarLocale}
        onChange={(value: LOCALE_OPTIONS) => {
          dispatch(
            handleSaveWidgetTextDefaultDetail({
              calendarLocale: value,
            }),
          );
        }}
      />
      <RevertDefaultModal
        open={isOpenModal}
        onCancel={() => setIsOpenModal(false)}
        onSubmit={revertDefault}
        loading={isLoadingRevertText}
      />
    </BlockStack>
  );
};
